import {
  Product,
  SeatMapSlotResource,
  ResourceCategory,
  Scheduling,
  SellingMode,
  ResourceCategoryCompleteCapacityChargeTypeEnum,
  SeatMapOverview,
  SeatMapSlot,
  SeatMapSlotTypeEnum,
  Tariff,
} from '@src/core/buyerApiClient';
import type { FullProduct, TariffsPerType } from '@src/features/product';
import { schedulingContainsMinute } from '@src/common/schedulings';
import type { SelectedTariff } from '@src/common/tariffs';

export const UNAVAILABLE_STATUS = 'unavailable';
export const AVAILABLE_STATUS = 'available';
export const SELECTED_STATUS = 'selected';

export const seatStatus = [AVAILABLE_STATUS, SELECTED_STATUS, UNAVAILABLE_STATUS] as const;

export const statusColors: { [key: string]: string } = {
  [AVAILABLE_STATUS]: '#34a952',
  [SELECTED_STATUS]: '#fbbb04',
  [UNAVAILABLE_STATUS]: '#aaaaaa',
};

export const isValidStatus = (stat: string) => [AVAILABLE_STATUS, SELECTED_STATUS].includes(stat);

const isDateAndTime = (prod: Product) => prod.sellingMode === SellingMode.WithDateAndTime;
const isDateOnly = (prod: Product) => prod.sellingMode === SellingMode.WithDateOnly;

export const getSchedulingFrom = (
  product: Product,
  tariffsPerType: TariffsPerType[],
  minutes?: number | null,
): Scheduling | undefined => {
  for (const tariffPerType of tariffsPerType) {
    if (tariffPerType.selectedQuantity === 0) {
      continue;
    }

    for (const scheduling of tariffPerType.schedulings) {
      if (
        isDateOnly(product) ||
        (isDateAndTime(product) && minutes != null && schedulingContainsMinute(scheduling, minutes))
      ) {
        return scheduling;
      }
    }
  }

  return undefined;
};

export const getResourceValueCents = (resource: SeatMapSlotResource, category: ResourceCategory) => {
  switch (category.completeCapacityChargeType) {
    case ResourceCategoryCompleteCapacityChargeTypeEnum.FixedValue:
      return resource.capacity * category.minCapacityUnitCents!;

    case ResourceCategoryCompleteCapacityChargeTypeEnum.TariffPercent:
      return Math.ceil(category.tariff!.priceCents! * (category.tariffPercentage! / 100)) * resource.capacity;

    default:
      return 0;
  }
};

export const getResourceAnalyticsData = (product: FullProduct, resource: SeatMapSlotResource) => ({
  productId: product?.id ?? '',
  productName: product?.name ?? '',
  resourceName: resource.name,
  resourceId: resource.id,
});

export const getDefaultAvailableArea = (
  areas: SeatMapOverview['areas'],
  resourceStatus: (slot: SeatMapSlotResource) => string,
) => {
  if (areas.length > 1) {
    for (const area of areas) {
      if (area.slots.some((slot) => resourceStatus(slot.resource!) === AVAILABLE_STATUS)) {
        return area;
      }
    }
  }

  return areas.length ? areas[0] : null;
};

export const getResourceMinCapacity = (category: ResourceCategory, resource: SeatMapSlotResource): number => {
  return category.allowUnderAllocation
    ? Math.ceil((category.minCapacityPercent! / 100) * resource.capacity)
    : resource.capacity;
};

interface ResourceTariffValues {
  [key: string]: number;
}

export const makeResourceTariffValues = (
  selectedTariffs: SelectedTariff[],
  resources: SeatMapSlotResource[],
  categoryIndex: { [key: string]: ResourceCategory },
): ResourceTariffValues => {
  const newTariffValues: ResourceTariffValues = {};
  const tariffPending = selectedTariffs.reduce(
    (carry: { [key: string]: number }, selTariff) => ({
      ...carry,
      [`${selTariff.tariff.id}`]: selTariff.quantity,
    }),
    {},
  );

  resources.sort((a, b) => {
    const catA = categoryIndex[a.categoryId]!;
    const catB = categoryIndex[b.categoryId]!;

    if (
      catA.allowUnderAllocation &&
      catB.allowUnderAllocation &&
      (catA.minCapacityPercent || catB.minCapacityPercent)
    ) {
      if (catA.minCapacityPercent && !catB.minCapacityPercent) {
        return -1;
      }

      if (!catA.minCapacityPercent && catB.minCapacityPercent) {
        return 1;
      }

      const extraAmountA = a.capacity - getResourceMinCapacity(catA, a);
      const extraAmountB = b.capacity - getResourceMinCapacity(catB, b);

      return extraAmountA - extraAmountB;
    }

    if (catA.allowUnderAllocation === catB.allowUnderAllocation) {
      return 0;
    }

    return !catA.allowUnderAllocation ? -1 : 1;
  });

  // We run this block twice to ensure the minimum capacity is allocated first for each resource
  for (let x = 0; x <= 1; x++) {
    for (const res of resources) {
      const category = categoryIndex[res.categoryId]!;
      const minAmount = getResourceMinCapacity(category, res);
      let availableCapacity = x === 0 ? minAmount : res.capacity - minAmount; // In the first iteration, we allocate the minimum. In the second, we allocate the rest

      for (const selTariff of selectedTariffs) {
        const tariffKey = `${selTariff.tariff.id}`;
        const currQuantity = tariffPending[tariffKey]!;

        const amount = Math.min(availableCapacity, currQuantity);
        if (amount === 0) {
          continue;
        }

        availableCapacity -= amount;
        const key = makeKey(res, selTariff.tariff);
        newTariffValues[key] = (newTariffValues[key] ?? 0) + amount;
        tariffPending[tariffKey] = currQuantity - amount;
      }
    }
  }

  return newTariffValues;
};

export const getResourceTariffValuesQuantity = (
  res: SeatMapSlotResource,
  tariff: Tariff,
  tariffValues: ResourceTariffValues,
): number => tariffValues[makeKey(res, tariff)] ?? 0;

const makeKey = (res: SeatMapSlotResource, tariff: Tariff): string => `${res.id}_${tariff.id}`;

export const getTotalCapacity = (resources: SeatMapSlotResource[]): number =>
  resources.reduce((sum, res) => sum + res.capacity, 0);

export const treeshakeUnnecessaryResources = (
  resources: SeatMapSlotResource[],
  tariffSum: number,
): SeatMapSlotResource[] => {
  const totalCapacity = getTotalCapacity(resources);
  let leftover = totalCapacity - tariffSum;

  const minorResources = resources.filter((res) => res.capacity <= leftover);
  minorResources.sort((a, b) => b.capacity - a.capacity);

  const toRemove: { [key: string]: boolean } = {};
  for (const res of minorResources) {
    if (leftover < res.capacity) {
      break;
    }

    leftover -= res.capacity;
    toRemove[res.id] = true;
  }

  return resources.filter((res) => !toRemove[res.id]);
};

export const seatMapResourceList = (seatMap: SeatMapOverview): SeatMapSlotResource[] => {
  const allSlots = seatMap.areas.reduce((carry, area) => [...carry, ...area.slots], [] as SeatMapSlot[]);
  return allSlots.filter((s) => s.type === SeatMapSlotTypeEnum.Resource).map((s) => s.resource!);
};

export const getMovableRemainingQuantity = (
  selectedResources: SeatMapSlotResource[],
  selectedTariffs: SelectedTariff[],
  categoryIndex: { [key: string]: ResourceCategory },
  tariffValues: ResourceTariffValues,
): number => {
  return selectedResources.reduce((sum, res) => {
    const filledAmount = selectedTariffs.reduce(
      (sum, { tariff }) => sum + getResourceTariffValuesQuantity(res, tariff, tariffValues),
      0,
    );
    const minAmount = getResourceMinCapacity(categoryIndex[res.categoryId]!, res);

    return sum + Math.max(filledAmount - minAmount, 0);
  }, 0);
};

export const getMaxAllowedCapacityForResourceList = (
  resourceList: SeatMapSlotResource[],
  remainingQuantityToAllocate: number,
): number | null => {
  return resourceList.reduce(
    (curr: number | null, { capacity: cap }) =>
      cap > remainingQuantityToAllocate && (curr === null || cap < curr) ? cap : curr,
    null,
  );
};

export const getResourceStatus = (
  resource: SeatMapSlotResource,
  selectedResourceIds: string[],
  tariffSum: number,
  categoryIndex: { [key: string]: ResourceCategory },
  maxCapacity: number | null,
  selectedCapacitySum: number,
  remainingQuantityToAllocate: number,
  movableRemainingQuantity: number,
): string => {
  if (selectedResourceIds.includes(resource.id)) {
    return SELECTED_STATUS;
  }

  if (resource.taken) {
    return UNAVAILABLE_STATUS;
  }

  if (selectedCapacitySum >= tariffSum) {
    return UNAVAILABLE_STATUS;
  }

  const category = categoryIndex[resource.categoryId]!;

  if (category.blocked) {
    return UNAVAILABLE_STATUS;
  }

  if (!category.allowUnderAllocation && maxCapacity && resource.capacity > maxCapacity) {
    // TODO adjust
    return UNAVAILABLE_STATUS;
  }

  if (remainingQuantityToAllocate + movableRemainingQuantity < getResourceMinCapacity(category, resource)) {
    return UNAVAILABLE_STATUS;
  }

  return AVAILABLE_STATUS;
};

export interface AllocatedResource {
  resource: SeatMapSlotResource;
  category: ResourceCategory;
  filledAmount: number;
  incomplete: boolean;
  isReady: boolean;
  hasMinValue: boolean;
  resourceValueCents: number;
  shouldShowSubAllocationNotification: boolean;
}

export const getAllocatedResources = (
  selectedResources: SeatMapSlotResource[],
  selectedTariffs: SelectedTariff[],
  categoryIndex: { [key: string]: ResourceCategory },
  tariffValues: ResourceTariffValues,
  remainingQuantityToAllocate: number,
): AllocatedResource[] => {
  return selectedResources.map((resource) => {
    const filledAmount = selectedTariffs.reduce(
      (sum, { tariff }) => sum + getResourceTariffValuesQuantity(resource, tariff, tariffValues),
      0,
    );
    const incomplete = filledAmount < resource.capacity;
    const category = categoryIndex[resource.categoryId]!;
    const isReady = category.allowUnderAllocation || !incomplete;
    const chargeType = category.completeCapacityChargeType;
    const hasMinValue =
      chargeType === ResourceCategoryCompleteCapacityChargeTypeEnum.FixedValue ||
      chargeType === ResourceCategoryCompleteCapacityChargeTypeEnum.TariffPercent;
    const resourceValueCents = getResourceValueCents(resource, category);
    const shouldShowSubAllocationNotification = !!(
      category.allowUnderAllocation &&
      category.minCapacityPercent &&
      remainingQuantityToAllocate > 0 &&
      remainingQuantityToAllocate < getResourceMinCapacity(category, resource)
    );

    return {
      resource,
      category,
      filledAmount,
      incomplete,
      isReady,
      hasMinValue,
      chargeType,
      resourceValueCents,
      shouldShowSubAllocationNotification,
    };
  });
};

export type SeatMapAllocationAttempt = {
  success: boolean;
  missingCapacity: number; // The amount of resource capacity missing to fill the selected tariffs
  missingTariffs: number; // The amount of tariffs missing to fill the available resources
};

export const getSeatMapBestAllocationStatus = (
  seatMap: SeatMapOverview,
  selectedTariffs: SelectedTariff[],
): SeatMapAllocationAttempt => {
  const availableResourceList = seatMapResourceList(seatMap).filter((res) => !res.taken);
  const categoryIndex = seatMap.categories.reduce((carry, cat) => ({ ...carry, [cat.id]: cat }), {});
  const totalToFill = selectedTariffs.reduce((sum, { quantity }) => sum + quantity, 0); // Total amount of tariffs to fill

  return getBestAllocationForResources([], availableResourceList, selectedTariffs, totalToFill, categoryIndex);
};

const getBestAllocationForResources = (
  selected: SeatMapSlotResource[],
  remaining: SeatMapSlotResource[],
  selectedTariffs: SelectedTariff[],
  totalToFill: number,
  categoryIndex: { [key: string]: ResourceCategory },
  cache: { [key: string]: SeatMapAllocationAttempt } = {},
): SeatMapAllocationAttempt => {
  selected.sort((a, b) => a.id.localeCompare(b.id));
  const cacheKey = selected.map((res) => res.id).join('-');
  if (cache[cacheKey]) {
    return cache[cacheKey]!;
  }

  const selectedCapacitySum = getTotalCapacity(selected); // Total capacity of the selected resources
  const tariffValues = makeResourceTariffValues(selectedTariffs, selected, categoryIndex); // Calculate the amount of each tariff in each resource

  const filledAmount = selected.reduce((carry, res) => {
    return (
      carry +
      selectedTariffs.reduce((sum, { tariff }) => sum + getResourceTariffValuesQuantity(res, tariff, tariffValues), 0)
    );
  }, 0); // Total amount of tariffs filled

  const missingCapacity = totalToFill - filledAmount;
  const missingTariffs = selected.reduce((sum, res) => {
    const minAmount = getResourceMinCapacity(categoryIndex[res.categoryId]!, res);
    const filled = selectedTariffs.reduce(
      (sum, { tariff }) => sum + getResourceTariffValuesQuantity(res, tariff, tariffValues),
      0,
    );

    return sum + Math.max(minAmount - filled, 0);
  }, 0); // Amount missing to fill the minimum capacity of all the selected resources

  let currentAlloc = {
    success: missingCapacity === 0 && missingTariffs === 0,
    missingCapacity,
    missingTariffs,
  };

  if (currentAlloc.success) {
    return currentAlloc;
  }

  for (const seat of remaining) {
    const newSelected = [...selected, seat];
    const minimalResources = treeshakeUnnecessaryResources(newSelected, totalToFill);
    const seatWasIgnored = !minimalResources.find((s) => s.id === seat.id);

    if (seatWasIgnored) {
      continue;
    }

    const remainingSeats = remaining.filter((s) => s.id !== seat.id);
    const newAlloc = getBestAllocationForResources(
      minimalResources,
      remainingSeats,
      selectedTariffs,
      totalToFill,
      categoryIndex,
      cache,
    );

    if (newAlloc.success) {
      return newAlloc;
    }

    // If new allocation is better than the current one, update the current allocation
    if (
      (currentAlloc.missingTariffs === 0 && newAlloc.missingTariffs > 0) ||
      (newAlloc.missingTariffs > 0 && newAlloc.missingTariffs < currentAlloc.missingTariffs) ||
      (newAlloc.missingTariffs === currentAlloc.missingTariffs &&
        newAlloc.missingCapacity < currentAlloc.missingCapacity)
    ) {
      currentAlloc = newAlloc;
    }
  }

  cache[cacheKey] = currentAlloc;

  return currentAlloc;
};
